import {
  CoordinateSystem,
  LocalFrameAxes,
  RotationConvention,
  deriveAxesFromDefinition
} from './local-frame.util';

/**
 * The convention every Euler definition created in the editor is written
 * with. Definitions without one are read as the older global-axes order.
 */
export const NEW_SYSTEM_ROTATION_CONVENTION: RotationConvention = 'intrinsic-xyz';

/**
 * Okabe-Ito, chosen because the eight hues stay distinguishable under the
 * common forms of colour blindness without needing a separate safe mode.
 */
const SYSTEM_COLORS = [
  '#0072b2',
  '#e69f00',
  '#009e73',
  '#cc79a7',
  '#d55e00',
  '#56b4e9',
  '#f0e442',
  '#000000'
];

export function coordinateSystemColor(index: number): string {
  return SYSTEM_COLORS[((index % SYSTEM_COLORS.length) + SYSTEM_COLORS.length) % SYSTEM_COLORS.length];
}

export function colorForSystemId(systems: CoordinateSystem[], id: string | null): string | null {
  if (!id) return null;
  const index = systems.findIndex((system) => system.id === id);
  return index === -1 ? null : coordinateSystemColor(index);
}

/** The label and swatch colour for a load/BC's assigned frame, or null for the global frame. */
export function frameBadgeFor(
  systems: CoordinateSystem[],
  id: string | null | undefined
): { name: string; color: string } | null {
  if (!id) return null;
  const system = systems.find((candidate) => candidate.id === id);
  if (!system) return null;
  // A degenerate frame draws on global axes, so it must not badge as framed.
  if (!deriveAxesFromDefinition(system.definition)) return null;
  const color = colorForSystemId(systems, system.id);
  return color ? { name: system.name, color } : null;
}

export interface CoordinateSystemUsage {
  loadcaseId: string;
  loadcaseName: string;
  description: string;
}

interface UsageScanItem {
  coordinateSystemId?: string | null;
}

interface UsageScanLoadcase {
  name?: string;
  id?: string;
  loads?: (UsageScanItem & { partId?: string })[];
  groups?: (UsageScanItem & { id?: string })[];
  boundaryConditions?: (UsageScanItem & { partId?: string })[];
}

/**
 * Everywhere a system is referenced. Deleting a system that still has usages
 * would silently reinterpret those components as global, so the list is shown
 * to the user instead of cascading.
 */
export function findCoordinateSystemUsage(
  loadcases: UsageScanLoadcase[],
  systemId: string
): CoordinateSystemUsage[] {
  const usage: CoordinateSystemUsage[] = [];

  for (const loadcase of loadcases ?? []) {
    const loadcaseName = loadcase.name || loadcase.id || 'Load case';
    const loadcaseId = loadcase.id ?? '';

    for (const load of loadcase.loads ?? []) {
      if (load.coordinateSystemId === systemId) {
        usage.push({ loadcaseId, loadcaseName, description: `Load on part ${load.partId ?? '?'}` });
      }
    }
    for (const group of loadcase.groups ?? []) {
      if (group.coordinateSystemId === systemId) {
        usage.push({ loadcaseId, loadcaseName, description: `Load group ${group.id ?? '?'}` });
      }
    }
    for (const bc of loadcase.boundaryConditions ?? []) {
      if (bc.coordinateSystemId === systemId) {
        usage.push({ loadcaseId, loadcaseName, description: `Boundary condition on part ${bc.partId ?? '?'}` });
      }
    }
  }

  return usage;
}

export function uniqueCoordinateSystemName(existing: CoordinateSystem[]): string {
  const taken = new Set(existing.map((system) => system.name.trim().toLowerCase()));
  let index = existing.length + 1;
  let candidate = `Coordinate system ${index}`;
  while (taken.has(candidate.toLowerCase())) {
    index += 1;
    candidate = `Coordinate system ${index}`;
  }
  return candidate;
}

export function createCoordinateSystem(existing: CoordinateSystem[]): CoordinateSystem {
  return {
    id: `cs-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    name: uniqueCoordinateSystemName(existing),
    definition: {
      mode: 'euler',
      origin: [0, 0, 0],
      rotation: [0, 0, 0],
      rotationConvention: NEW_SYSTEM_ROTATION_CONVENTION
    }
  };
}

export function hasDuplicateName(systems: CoordinateSystem[], system: CoordinateSystem): boolean {
  const name = system.name.trim().toLowerCase();
  if (!name) return false;
  return systems.some((other) => other.id !== system.id && other.name.trim().toLowerCase() === name);
}

export function duplicateCoordinateSystem(
  system: CoordinateSystem,
  existing: CoordinateSystem[]
): CoordinateSystem {
  return {
    id: `cs-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    name: uniqueCoordinateSystemName(existing),
    definition: structuredClone(system.definition)
  };
}

function radToDeg(rad: number): number {
  return (rad * 180) / Math.PI;
}

/**
 * Angles in degrees, X, Y, Z, for the rotation whose columns are `axes`, in
 * the given convention - the inverse of the composition deriveFramePoints
 * uses.
 *
 * When the middle (Y) rotation is +-90 degrees only the sum or difference of
 * the other two angles is determined; the X rotation is pinned to zero.
 */
export function eulerFromAxes(axes: LocalFrameAxes, convention: RotationConvention): number[] {
  const clamp = (n: number): number => Math.min(1, Math.max(-1, n));

  if (convention === 'intrinsic-xyz') {
    // R = Rx * Ry * Rz: R02 = sin(y), R12 = -sin(x)cos(y), R22 = cos(x)cos(y),
    // R00 = cos(y)cos(z), R01 = -cos(y)sin(z).
    const ry = Math.asin(clamp(axes.ez[0]));
    if (Math.abs(Math.cos(ry)) < 1e-6) {
      return [0, radToDeg(ry), radToDeg(Math.atan2(axes.ex[1], axes.ey[1]))];
    }
    return [
      radToDeg(Math.atan2(-axes.ez[1], axes.ez[2])),
      radToDeg(ry),
      radToDeg(Math.atan2(-axes.ey[0], axes.ex[0]))
    ];
  }

  // R = Rz * Ry * Rx: R20 = -sin(y), R21 = cos(y)sin(x), R22 = cos(y)cos(x),
  // R10 = sin(z)cos(y), R00 = cos(z)cos(y).
  const ry = Math.asin(clamp(-axes.ex[2]));
  if (Math.abs(Math.cos(ry)) < 1e-6) {
    return [0, radToDeg(ry), radToDeg(Math.atan2(-axes.ey[0], axes.ey[1]))];
  }
  return [
    radToDeg(Math.atan2(axes.ey[2], axes.ez[2])),
    radToDeg(ry),
    radToDeg(Math.atan2(axes.ex[1], axes.ex[0]))
  ];
}
